import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { OpportunityActivityEntry } from "./OpportunityActivityEntry";
import type { ActivityLogTurnEntry } from "./opportunityActivityEntries";
import type { ActivityNames } from "./opportunityActivitySentences";

interface OpportunityActivityTurnProps {
  turn: ActivityLogTurnEntry;
  names: ActivityNames;
  onOpenThread: (threadId: string) => void;
  onOpenQuote: (quoteId: string) => void;
}

/**
 * An agent turn as one line, its first step, with the later steps behind a
 * disclosure that starts closed.
 * @param turn - The folded agent turn
 * @param names - The names ids resolve through
 * @param onOpenThread - Opens an agent thread
 * @param onOpenQuote - Opens a quote
 */
export const OpportunityActivityTurn = ({
  turn,
  names,
  onOpenThread,
  onOpenQuote,
}: OpportunityActivityTurnProps) => (
  <Collapsible>
    <OpportunityActivityEntry
      event={turn.lead}
      viewCount={1}
      names={names}
      onOpenThread={onOpenThread}
      onOpenQuote={onOpenQuote}
      trailing={
        <>
          <span className="text-muted-foreground">·</span>{" "}
          <CollapsibleTrigger className="group -mx-1 inline-flex items-baseline gap-1 rounded-sm px-1 font-semibold hover:bg-muted focus-visible:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted">
            {turn.steps.length} {turn.steps.length === 1 ? "step" : "steps"}
            <ChevronDown
              aria-hidden
              className="relative top-0.5 h-3 w-3 text-muted-foreground motion-safe:transition-transform group-data-[state=open]:rotate-180"
            />
          </CollapsibleTrigger>
        </>
      }
    />
    {/* Layout lives on an inner div: a display utility on the content itself
        would override the hidden attribute Radix sets when closed. */}
    <CollapsibleContent>
      <div className="ml-[2.75rem] flex flex-col gap-1.5 border-l-2 border-border pl-1">
        {turn.steps.map((step) => (
          <OpportunityActivityEntry
            key={step.id}
            event={step}
            viewCount={1}
            names={names}
            showActorName={false}
            onOpenThread={onOpenThread}
            onOpenQuote={onOpenQuote}
          />
        ))}
      </div>
    </CollapsibleContent>
  </Collapsible>
);
